import { RecordingStateIdle } from '@/features/nectar/components/audio-recording/RecordingStateIdle'
import { RecordingStateRecorded } from '@/features/nectar/components/audio-recording/RecordingStateRecorded'
import { RecordingStateRecording } from '@/features/nectar/components/audio-recording/RecordingStateRecording'
import { useTurnstileCaptcha } from '@/features/turnstile'
import { Button, Text } from '@/ui-kit'
import { Trans } from '@lingui/react/macro'
import type { FreeSpeechTaskPayload, FreeSpeechTypeConfig, TaskStats } from '../../api/models'
import { useAudioRecorder } from '../../hooks/useAudioRecorder'
import { useRecordingQualityAnalytics } from '../../hooks/useRecordingQualityAnalytics'
import { useSubmitAudioRecording } from '../../hooks/useSubmitAudioRecording'

type FreeSpeechTaskBodyProps = {
  task: FreeSpeechTaskPayload
  taskIndex: number
  projectId: string
  typeConfig: FreeSpeechTypeConfig
  stats: TaskStats
  captchaEnabled: boolean
  onSkipRequest: (taskId: string) => void
}

export function FreeSpeechTaskBody({
  task,
  taskIndex,
  projectId,
  typeConfig,
  stats,
  captchaEnabled,
  onSkipRequest
}: FreeSpeechTaskBodyProps) {
  const recorder = useAudioRecorder({ captureMetadata: true })
  const captcha = useTurnstileCaptcha({ enabled: captchaEnabled, action: 'submission' })
  const submitAudioRecording = useSubmitAudioRecording({ recorder, captcha })

  const { state, seconds, audioUrl, blob, startRecording, stopRecording, reset, quality, analyser } = recorder
  useRecordingQualityAnalytics(projectId, quality)

  const topic = typeof task.params?.topic === 'string' ? task.params.topic : ''
  const hint = typeof task.params?.hint === 'string' ? task.params.hint : ''

  const onSubmit = () => {
    if (!blob) return
    submitAudioRecording({
      projectId,
      taskId: task.id,
      taskIndex,
      blob,
      seconds
    })
  }

  const isRecordedState = state === 'recorded' || state === 'submitting' || state === 'submitted'
  const submitDisabled = captcha.active && !captcha.token
  const skipVisible = typeConfig.maxSkipsPerUser > 0 && state === 'idle'
  const overMax = seconds >= typeConfig.maxDurationSec

  return (
    <div style={{ animation: 'nectar-slide-up 0.4s ease-out' }}>
      <div className="flex flex-col items-center gap-6 rounded-2xl border border-solid border-gray-100 px-8 py-10">
        {task.instruction && (
          <Text
            type="body2-regular"
            className="text-gray-90 m-auto block rounded-xl border border-solid border-gray-100 p-2"
          >
            {task.instruction}
          </Text>
        )}
        {topic && <span className="text-h5 block text-center leading-[120%]">&ldquo;{topic}&rdquo;</span>}
        {hint && (
          <Text type="body3-regular" className="text-gray-80 text-center">
            {hint}
          </Text>
        )}
        <div className="flex w-full flex-col items-center gap-5">
          {state === 'idle' && (
            <>
              <RecordingStateIdle onStartRecording={startRecording} />
              <Text type="body3-regular" className="text-gray-80">
                Speak for {typeConfig.minDurationSec}–{typeConfig.maxDurationSec} seconds
              </Text>
            </>
          )}
          {state === 'recording' && (
            <>
              <RecordingStateRecording seconds={seconds} analyser={analyser} onStopRecording={stopRecording} />
              {overMax ? (
                <Text type="body3-regular" className="text-red">
                  <Trans>You can stop now</Trans>
                </Text>
              ) : (
                <Text type="body3-regular" className="text-gray-80">
                  {typeConfig.maxDurationSec - seconds}s left
                </Text>
              )}
            </>
          )}
          {isRecordedState && (
            <>
              {captcha.renderWidget('w-full')}
              <RecordingStateRecorded
                audioUrl={audioUrl}
                seconds={seconds}
                isSubmitting={state === 'submitting'}
                minSeconds={typeConfig.minDurationSec}
                submitDisabled={submitDisabled}
                quality={quality}
                onReRecord={() => {
                  captcha.reset()
                  reset()
                  startRecording()
                }}
                onSubmit={onSubmit}
              />
            </>
          )}
          {skipVisible && (
            <Button
              variant="bordered-gray"
              size="small"
              isDisabled={stats.skipsRemaining === 0}
              onClick={() => onSkipRequest(task.id)}
            >
              <Trans>Try another task</Trans>
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
